import * as React from 'react';

/** The button's label: it imports the last 30 days, then they are scanned. */
export const SYNC_LABEL = 'Sync';

/** The button's tooltip while idle: what a click does. */
export const SYNC_HINT = 'Fetch your chess.com games of the last 30 days and scan them';

/** What the button is doing: waiting for a click, importing, or scanning what came in. */
export type ImportPhase = 'idle' | 'importing' | 'scanning';

export interface ImportButtonProps {
  phase: ImportPhase;
  /** The status line beside the button; empty for none. */
  status: string;
  /** The status is a failure. */
  error?: boolean;
  /** Whether a click may start an import (an account is set, nothing runs). */
  enabled: boolean;
  /** Games imported so far, while importing. */
  imported?: number;
  /** The automatic scan, while scanning: games finished and the batch's size. */
  scanned?: number;
  total?: number;
  /** "~4 min": the time the games left would take. */
  estimate?: string;
  /** Milliseconds the button still waits after the last press (the source is read at most once a minute); 0 or absent when it may be pressed. */
  cooling?: number;
  /** The newest game of the last sync, once scanned: the status becomes this link to its review (2026-10-14). */
  ready?: { label: string; href: string; onOpen: () => void };
  onImport: () => void;
}

/**
 * The importer's button, "Sync", in every screen's top bar, the screen's
 * primary action (2026-10-14: it is the main flow, a game played and synced
 * to be seen), with the status line beside it: idle it reads "Sync" and the
 * line says when the last sync was, then the import's progress, then the
 * automatic scan's ("Scanning 3 of 12 · ~4 min") with a bar under it, and,
 * once the newest game of the sync is scanned, a link to its review. One
 * button for both, since the scan follows every import on its own; a click
 * is taken only while neither runs, and not within a minute of the last
 * (the button is disabled, its tooltip saying for how long; nothing about
 * the source's limits is shown).
 */
export const ImportButton: React.FC<ImportButtonProps> = ({
  phase,
  status,
  error,
  enabled,
  imported,
  scanned = 0,
  total = 0,
  estimate,
  cooling = 0,
  ready,
  onImport,
}) => {
  const coolingTitle = cooling > 0 ? `Synced just now · again in ${Math.ceil(cooling / 1000)} s` : undefined;
  const label =
    phase === 'importing'
      ? `Importing… ${imported ?? 0} ${imported === 1 ? 'game' : 'games'}`
      : phase === 'scanning'
        ? `Scanning ${Math.min(scanned + 1, total)} of ${total}`
        : SYNC_LABEL;
  return (
    <div className="import-chesscom">
      <span className={`import-chesscom-status${error ? ' import-chesscom-error' : ''}`} role="status" title={ready ? ready.label : status}>
        {ready ? (
          <a
            className="import-chesscom-ready"
            href={ready.href}
            onClick={(e) => {
              if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
              e.preventDefault();
              ready.onOpen();
            }}>
            {ready.label}
          </a>
        ) : (
          status
        )}
      </span>
      <span className="import-chesscom-control">
        <button
          type="button"
          className="btn btn-primary import-chesscom-button"
          disabled={phase !== 'idle' || !enabled || cooling > 0}
          aria-busy={phase !== 'idle'}
          title={phase === 'scanning' ? 'The engine is scanning the games of the last 30 days, one at a time' : (coolingTitle ?? SYNC_HINT)}
          onClick={onImport}>
          {label}
          {phase === 'scanning' && estimate && <span className="import-chesscom-meta"> · {estimate}</span>}
        </button>
        {phase === 'scanning' && (
          <span className="import-chesscom-progress" aria-hidden="true">
            <span style={{ width: `${total ? (100 * scanned) / total : 0}%` }} />
          </span>
        )}
      </span>
    </div>
  );
};
